import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog as Base } from '@base-ui/react/dialog';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { CANVAS_UI } from './canvas-ui';
import { Icon } from '../../ui/icons';
import { IconButton, ListItem } from '../../ui';
import type { MainMenuEntry } from './main-menu';

/** Runs `run` once the sheet has closed, so a dialog it opens keeps the focus. */
const after = (run: () => void) => setTimeout(run, 0);

/**
 * The main menu on a phone (P5f, backlog §14.4): a full-screen sheet over the same model as the desktop menu
 * (`mainMenu()`), one level at a time; a submenu opens in place with a way back. Rows are 48 px (finger-sized).
 */
export function MenuSheet({ entries, onActions, onClose }: { entries: MainMenuEntry[]; onActions: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [stack, setStack] = useState<{ label: string; list: MainMenuEntry[] }[]>([]);
  const popup = useRef<HTMLDivElement>(null);
  const level = stack.at(-1);
  const list = level?.list ?? entries;
  const close = (run?: () => void) => {
    onClose();
    if (run) after(run);
  };
  return (
    <Base.Root open onOpenChange={(open) => !open && onClose()}>
      <Base.Portal>
        <Base.Popup
          ref={popup}
          // The sheet itself takes the focus, not its first button (its tooltip would open and catch Escape).
          initialFocus={popup}
          {...{ [CANVAS_UI]: '' }}
          data-menu-sheet=""
          className="os-dark os-sheet fixed inset-0 z-50 flex flex-col bg-surface pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] font-sans text-fg outline-none"
        >
          <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
            {level ? (
              <IconButton label={t('mainMenu.back')} icon={<Icon icon={ChevronLeft} size={20} />} onClick={() => setStack((s) => s.slice(0, -1))} className="rounded-full" />
            ) : (
              <span className="size-11 shrink-0" />
            )}
            <Base.Title className="grow truncate text-center font-display text-base font-semibold">{level?.label ?? t('mainMenu.open')}</Base.Title>
            <IconButton label={t('mainMenu.close')} icon={<Icon icon={X} size={20} />} onClick={() => close()} className="rounded-full" />
          </div>
          {/* Rows never shrink: a short screen (a phone turned sideways) scrolls the list instead (P15 @regression). */}
          <div className="flex min-h-0 grow flex-col gap-0.5 overflow-y-auto overscroll-contain px-2 py-2 *:shrink-0">
            {!level && (
              <ListItem size="lg" icon={<Icon icon={Search} size={18} />} label={t('mainMenu.actions')} onClick={() => close(onActions)} className="mb-1 border border-border bg-surface-2 text-fg-muted" />
            )}
            {list.map((entry, i) =>
              'separator' in entry ? (
                <hr key={`sep-${i}`} className="my-1 border-border" />
              ) : 'children' in entry ? (
                <ListItem
                  key={entry.id}
                  size="lg"
                  label={entry.label}
                  hint={<Icon icon={ChevronRight} size={18} />}
                  onClick={() => setStack((s) => [...s, { label: entry.label, list: entry.children }])}
                />
              ) : (
                <ListItem
                  key={entry.id}
                  size="lg"
                  label={entry.label}
                  hint={entry.checked ? <span aria-hidden="true">✓</span> : undefined}
                  disabled={entry.disabled}
                  onClick={() => close(entry.run)}
                  {...(entry.checked === undefined ? {} : { role: 'menuitemcheckbox', 'aria-checked': entry.checked })}
                />
              ),
            )}
          </div>
        </Base.Popup>
      </Base.Portal>
    </Base.Root>
  );
}
